/**
 * loop-video.js
 * Inline auto-looping product videos in the media gallery.
 *
 * Markup contract (rendered by media-gallery.liquid for media tagged [loop] in alt text):
 *   <video class="media-viewer__loop" muted loop playsinline preload="none"
 *          poster="..." data-loop-src="...mp4"></video>
 *
 * Behaviour:
 *   - Source is attached only when the clip scrolls into view (no wasted bytes on load).
 *   - Playback pauses when out of view.
 *   - Re-arms after the theme's global window.pauseAllMedia() stops it mid-view.
 *   - Respects prefers-reduced-motion: poster only, never plays.
 */
(() => {
  const init = () => {
    const vids = document.querySelectorAll('.media-viewer__loop:not([data-loop-init])');
    if (!vids.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const inView = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target: video, isIntersecting }) => {
        if (isIntersecting) {
          inView.add(video);
          if (!video.getAttribute('src') && video.dataset.loopSrc) {
            video.setAttribute('src', video.dataset.loopSrc);
          }
          video.play().catch(() => {});
        } else {
          inView.delete(video);
          video.pause();
        }
      });
    }, { threshold: 0.25 });

    vids.forEach((video) => {
      video.setAttribute('data-loop-init', 'true');

      // Safari needs these set as properties, not just attributes.
      video.muted = true;
      video.playsInline = true;

      if (reduced) return;

      observer.observe(video);

      // The gallery calls window.pauseAllMedia() on every scroll and variant change.
      // If the clip is still on screen, start it again.
      video.addEventListener('pause', () => {
        if (inView.has(video) && !video.ended) {
          video.play().catch(() => {});
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();